import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { CloudUploadIcon, FileIcon, XIcon } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { documentRedactorState } from "@/lib/document-redactor-state";
import {
  FALLBACK_ALLOWED_FILE_TYPES,
  acceptAttrFromAllowedTypes,
  allowedFileTypesQueryOptions,
  isFileTypeAllowed,
} from "@/lib/queries/pii-file-scan";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/document-redactor/")({
  component: RouteComponent,
  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(allowedFileTypesQueryOptions());
  },
});

function RouteComponent() {
  const [file, setFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  const allowedTypesQuery = useQuery(allowedFileTypesQueryOptions());
  const allowedTypes = allowedTypesQuery.data ?? FALLBACK_ALLOWED_FILE_TYPES;
  const accept = useMemo(
    () => acceptAttrFromAllowedTypes(allowedTypes),
    [allowedTypes],
  );

  function takeFile(next: File | undefined) {
    if (!next) return;
    if (!isFileTypeAllowed(next, allowedTypes)) {
      toast.error("This file type is not supported");
      return;
    }
    void router.preloadRoute({ from: Route.fullPath, to: "./options" });
    setFile(next);
  }

  return (
    <div>
      <div className="mb-8 space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Document Redactor</h1>
        <p className="text-muted-foreground">
          Masks/Highlights PIIs on your files
        </p>
      </div>

      <Card className="bg-gray-100 p-8 dark:bg-black">
        <div className="mb-8 text-center">
          <h2 className="text-xl font-semibold">
            Upload your document to start redacting
          </h2>
        </div>
        {file ? (
          <div className="flex justify-center">
            <div className="flex max-w-xl items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-black">
              <div className="flex items-center gap-4">
                <FileIcon />
                <div>
                  <p className="font-medium text-gray-700 dark:text-white">
                    {file.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="flex h-5 w-5 items-center justify-center rounded-sm bg-gray-700"
                aria-label="Remove file"
              >
                <XIcon size={18} className="text-white" />
              </button>
            </div>
          </div>
        ) : (
          <div
            data-drag-active={isDragActive ? "true" : undefined}
            className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-gray-200 bg-white p-12 data-[drag-active]:border-primary dark:border-gray-800 dark:bg-black"
            onClick={() => inputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              takeFile(event.dataTransfer.files[0]);
            }}
          >
            <div className="mb-4 rounded-full p-4">
              <CloudUploadIcon className="h-6 w-6 text-gray-500" />
            </div>

            <div className="space-y-2 text-center">
              <p>
                <button type="button" className="cursor-pointer">
                  <span className="font-medium text-primary dark:text-gray-500">
                    Click to upload your file
                  </span>
                </button>
              </p>
              <p className="text-sm text-muted-foreground">or drag and drop</p>
              <p className="text-xs text-muted-foreground">
                {accept.replaceAll(".", "").toUpperCase()}
              </p>
              <input
                ref={inputRef}
                type="file"
                id="fileInput"
                className="hidden"
                accept={accept}
                onChange={(event) => {
                  takeFile(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
            </div>
          </div>
        )}
      </Card>

      <div className="mt-8 flex justify-center">
        <Link
          className={cn(
            buttonVariants(),
            "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
            "bg-gray-700 px-8 hover:bg-gray-600",
          )}
          disabled={!file}
          from={Route.fullPath}
          to="./options"
          mask={{
            to: ".",
          }}
          onClick={() => {
            if (file) documentRedactorState.setFile(file);
          }}
        >
          Next
        </Link>
      </div>
    </div>
  );
}
